import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { img, inr } from "@/lib/api";
import { Cta } from "@/components/Cta";

const BG = { pearl: "bg-pearl", blush: "bg-blush", lilac: "bg-lilac", plum: "bg-plum" };
const MINH = { compact: "lg:min-h-[600px]", tall: "lg:min-h-[720px]", full: "lg:min-h-[calc(100vh-112px)]" };
const up = (d) => ({ initial: { opacity: 0, y: 26 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay: d, ease: [0.2, 0.8, 0.2, 1] } });

const splitHeading = (h) => {
  const parts = h.split(/(?<=\.)\s+/);
  return [parts[0], parts.slice(1).join(" ")];
};

const Thumbs = ({ products, dark }) => (
  <div className="flex items-center gap-4">
    <div className="flex -space-x-3">
      {products.slice(0, 3).map((p) => (
        <Link key={p.id} to={`/product/${p.slug}`} data-testid={`hero-thumb-${p.slug}`} className={`w-14 h-14 rounded-[14px] overflow-hidden ring-[3px] ${dark ? "ring-plum" : "ring-pearl"} hover:-translate-y-1 transition-transform`}>
          <img src={img(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
        </Link>
      ))}
    </div>
    <p className="text-[14px] leading-snug">
      <span className="font-extrabold block">The Selected Three</span>
      <span className="opacity-70">From {inr(Math.min(...products.map((p) => p.effective_price)))}</span>
    </p>
  </div>
);

export const Hero = () => {
  const { site, products } = useStore();
  const h = site.hero;
  const dark = h.background === "plum";
  const [a, b] = splitHeading(h.heading);
  const featured = products.find((p) => p.id === site.story.product_id) || products.find((p) => p.featured) || products[0];
  const right = h.align === "right";

  return (
    <section data-testid="hero-section" className={`${BG[h.background] || "bg-pearl"} ${dark ? "text-pearl" : "text-plum"} relative overflow-hidden`}>
      <div aria-hidden className="hidden lg:block absolute -left-40 top-24 w-[520px] h-[520px] rounded-full bg-lavender/50 blur-3xl" />
      <div className={`container-rh relative hidden lg:grid grid-cols-12 gap-12 items-center py-10 ${MINH[h.height] || MINH.tall}`}>
        <div className={`col-span-5 relative z-10 ${right ? "order-2" : ""}`}>
          <motion.p {...up(0.05)} className={`eyebrow flex items-center gap-3 ${dark ? "text-champagne" : "text-berry"}`}>
            <span className="w-10 h-px bg-current" />{h.eyebrow}
          </motion.p>
          <motion.h1 {...up(0.15)} data-testid="hero-heading" className="font-display font-medium text-6xl xl:text-[82px] leading-[0.98] tracking-[-0.01em] mt-7">
            {a}{b && <><br /><em className={`italic ${dark ? "text-champagne" : "text-berry"}`}>{b}</em></>}
          </motion.h1>
          <motion.p {...up(0.25)} className="mt-8 text-[18px] leading-relaxed max-w-[440px] opacity-80" data-testid="hero-description">{h.description}</motion.p>
          <motion.div {...up(0.35)} className="mt-10 flex flex-wrap items-center gap-7">
            <Cta to="/#edit" label={h.cta_primary} variant={dark ? "pearl" : "plum"} size="lg" testId="hero-cta-primary" />
            <Link to="/#story" className="rh-link" data-testid="hero-cta-secondary">{h.cta_secondary}</Link>
          </motion.div>
          <motion.div {...up(0.45)} className="mt-14"><Thumbs products={products} dark={dark} /></motion.div>
        </div>
        <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.3, ease: [0.2, 0.8, 0.2, 1] }} className={`col-span-7 relative py-4 ${right ? "order-1" : ""}`}>
          <div className="relative rounded-[28px] overflow-hidden w-fit max-w-full ml-auto">
            <img src={img(h.desktop_image)} alt="RATNAHARA campaign" fetchPriority="high" className="fit-view" />
            <div className="absolute inset-0 bg-plum" style={{ opacity: (h.overlay || 0) / 100 }} />
            <p className="absolute right-5 top-1/2 -translate-y-1/2 [writing-mode:vertical-rl] rotate-180 text-[11px] font-extrabold tracking-[0.42em] text-pearl/90">JEWELS THAT TELL YOUR STORY</p>
          </div>
          {featured && (
            <Link to={`/product/${featured.slug}`} data-testid="hero-featured-card" className={`group absolute bottom-14 ${right ? "-right-8" : "-left-10"} flex items-center gap-4 rounded-[16px] bg-ivory/95 backdrop-blur-xl p-3 pr-5 shadow-[0_24px_50px_-20px_rgba(48,32,45,.45)] text-plum`}>
              <img src={img(featured.images[0])} alt="" className="w-16 h-16 rounded-[12px] object-cover" />
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-berry">Hero piece</p>
                <p className="font-display text-[20px] font-semibold leading-tight">{featured.name}</p>
                <p className="text-[14px] font-bold">{inr(featured.effective_price)}</p>
              </div>
              <ArrowUpRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          )}
          <div aria-hidden className="absolute -top-6 -right-10 w-44 h-44 rounded-full border border-champagne" />
        </motion.div>
      </div>

      <div className="lg:hidden px-3 sm:px-6 pt-3 pb-12">
        <div className="relative rounded-[28px] overflow-hidden w-full">
          <img src={img(h.mobile_image || h.desktop_image)} alt="RATNAHARA campaign" fetchPriority="high" className="block w-full h-auto" />
          <div className="absolute inset-0 bg-plum pointer-events-none" style={{ opacity: (h.overlay || 0) / 100 }} />
          <p className="absolute left-4 top-4 facet rounded-[10px] bg-pearl/95 px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-plum">{h.eyebrow}</p>
        </div>
        <div className="px-2">
          <h1 className="font-display font-medium text-[42px] sm:text-[54px] leading-[1.02] mt-7" data-testid="hero-heading-mobile">
            {a}{b && <> <em className={`italic ${dark ? "text-champagne" : "text-berry"}`}>{b}</em></>}
          </h1>
          <p className="mt-6 text-[17px] leading-relaxed opacity-85">{h.description}</p>
          <div className="mt-7 grid gap-3">
            <Cta to="/#edit" label={h.cta_primary} full size="lg" variant={dark ? "pearl" : "plum"} testId="hero-cta-primary-mobile" />
            <Link to="/#story" className="h-[54px] rounded-[14px] border border-current/20 grid place-items-center text-[13px] font-extrabold uppercase tracking-[0.16em]" style={{ borderColor: "rgba(48,32,45,.2)" }}>{h.cta_secondary}</Link>
          </div>
          <div className="mt-8"><Thumbs products={products} dark={dark} /></div>
        </div>
      </div>
    </section>
  );
};
